import { operationIds } from "@gen/app-access-config";
import type {
  IClientFee,
  IPaymentMethod,
  IRate,
  IUpdateClientFeeBody,
} from "@gen/app-query";
import { Query, Rate, UpdateClientFeeBody } from "@gen/app-query";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import type { GridInitialState } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  type ChipOption,
  createChipViewCellRenderer,
  createFlagViewCellRenderer,
  EditableDataGrid,
  type EditableDataGridColDef,
  type FlagOption,
} from "~/components/editable_data_grid";
import { useCanExecuteOperation } from "~/contexts/auth_query";
import { clientFeeSchema } from "~/entities/client_fee";
import { useCurrencies } from "~/services/money";
import {
  parseMoney,
  renderMoneyWithoutCurrencySymbol,
} from "~/utilities/money";
import { formatDecimalAmount } from "~/utilities/number";
import { config } from "~root/config";

type FeeRow = Omit<
  IClientFee,
  "id" | "seller_rate" | "client_buyer_rate" | "client_seller_rate"
> & {
  id: string | number;
  seller_rate?: IRate;
  client_buyer_rate?: IRate;
  client_seller_rate?: IRate;
};

const getFlagName = (code: string): string =>
  `${code.slice(0, 2).toLowerCase()}.svg`;

export function ClientFees() {
  const locale = navigator.languages?.[0] ?? config.localization.locale;
  const canExecuteOperation = useCanExecuteOperation();
  const clientId = useParams({
    from: "/clients/$clientId/fees",
    select: (p) => p.clientId,
  });

  const canUpdateFee = canExecuteOperation(
    operationIds["core.updateClientFee"],
  );
  const canEditFees = canUpdateFee;

  const initialState: GridInitialState = {
    sorting: {
      sortModel: [
        {
          field: "charge_config",
          sort: "asc",
        },
      ],
    },
  };

  const { data: feesData, isFetching: isFeesFetching } =
    Query.useCore_getClientFeesQuery(clientId);

  const [editedFees, setEditedFees] = useState<FeeRow[]>([]);
  const [_editingFees, setEditingFees] = useState<Set<string | number>>(
    new Set(),
  );

  const queryClient = useQueryClient();

  const { mutate: updateFee } =
    Query.useCore_updateClientFeeMutationWithParameters({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: Query.core_getClientFeesQueryKey(clientId),
        });
      },
      meta: {
        success: "Fee updated",
      },
    });

  useEffect(() => {
    setEditedFees([]);
    setEditingFees(new Set());
  }, [feesData]);

  const { currencies } = useCurrencies();

  const { data: paymentMethodsData } =
    Query.useCore_getSupportedTransactionPaymentMethodsQuery();

  const paymentMethodChipOptions = useMemo<ChipOption[]>(() => {
    const methods = paymentMethodsData?.payment_methods ?? [];

    const toChipOption = (method: IPaymentMethod | null | undefined) => {
      if (!method) {
        return null;
      }
      const code = method.code;
      if (!code || code.length === 0) {
        return null;
      }
      return {
        value: code,
        label: method.label ?? code,
        fontColor: method.font_color ?? undefined,
        backgroundColor: method.background_color ?? undefined,
      };
    };

    return methods.reduce<ChipOption[]>((acc, method) => {
      const option = toChipOption(method);
      if (option) {
        acc.push(option);
      }
      return acc;
    }, []);
  }, [paymentMethodsData?.payment_methods]);
  const paymentMethodChipOptionMap = useMemo(() => {
    const map = new Map<string, ChipOption>();
    paymentMethodChipOptions.forEach((option) => {
      if (option.value.length === 0) {
        return;
      }
      map.set(option.value, option);
    });
    return map;
  }, [paymentMethodChipOptions]);

  const paymentMethodChipViewCell = useMemo(
    () => createChipViewCellRenderer<FeeRow>(paymentMethodChipOptionMap),
    [paymentMethodChipOptionMap],
  );

  const currencyFlagOptions = useMemo<FlagOption[]>(() => {
    if (!currencies || currencies.length === 0) {
      return [];
    }
    return currencies.map((currency) => {
      const value = currency.value ?? currency.code;
      return {
        value,
        label: currency.label,
        flagSrc: `/country_flags/${getFlagName(currency.code)}`,
      };
    });
  }, [currencies]);

  const currencyFlagOptionMap = useMemo(() => {
    const map = new Map<string, FlagOption>();
    currencyFlagOptions.forEach((option) => {
      if (option.value.length === 0) {
        return;
      }
      map.set(option.value, option);
    });
    return map;
  }, [currencyFlagOptions]);

  const currencyFlagViewCell = useMemo(
    () => createFlagViewCellRenderer<FeeRow>(currencyFlagOptionMap),
    [currencyFlagOptionMap],
  );
  const parseLocaleNumber = (value: unknown): number =>
    parseFloat(String(value).replace(",", "."));

  const columns: EditableDataGridColDef<FeeRow>[] = useMemo(
    () => [
      {
        field: "charge_config",
        headerName: "Config",
        editable: false,
        type: "number",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 60,
        minWidth: 60,
        disableColumnMenu: true,
        sortable: false,
        headerClassName: "no-sort-icon",
      },
      {
        field: "payment_method",
        headerName: "Payment Method",
        editable: false,
        type: "string",
        flex: 150,
        minWidth: 150,
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        renderViewCell: paymentMethodChipViewCell,
      },
      {
        field: "currency",
        headerName: "Currency",
        editable: false,
        type: "string",
        flex: 175,
        minWidth: 175,
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        renderCell: currencyFlagViewCell,
      },
      {
        field: "above",
        headerName: "Above",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 118,
        minWidth: 118,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(
            row.above ?? 0,
            row.currency,
            locale,
          ),
      },
      {
        field: "flat",
        headerName: "Buyer Trustap Fee Flat",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 172,
        minWidth: 172,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(row.flat ?? 0, row.currency, locale),
      },
      {
        field: "percentage",
        headerName: "Buyer Trustap Fee %",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 172,
        minWidth: 172,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          formatDecimalAmount(row.percentage ?? 0, locale),
      },
      {
        field: "seller_rate_flat",
        headerName: "Seller Trustap Fee Flat",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 172,
        minWidth: 172,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(
            row.seller_rate?.flat ?? 0,
            row.currency,
            locale,
          ),
      },
      {
        field: "seller_rate_percentage",
        headerName: "Seller Trustap Fee %",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 172,
        minWidth: 172,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          formatDecimalAmount(row.seller_rate?.percentage ?? 0, locale),
      },
      {
        field: "client_buyer_flat",
        headerName: "Buyer Commission Flat",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 110,
        minWidth: 160,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(
            row.client_buyer_rate?.flat ?? 0,
            row.currency,
            locale,
          ),
        valueSetter: (value: unknown, row: FeeRow) => {
          if (!row.currency) {
            return row;
          }
          return {
            ...row,
            client_buyer_rate: {
              ...(row.client_buyer_rate ?? {}),
              flat: parseMoney(String(value), row.currency, locale),
              percentage: row.client_buyer_rate?.percentage ?? 0,
            } as IRate,
          };
        },
      },
      {
        field: "client_buyer_percentage",
        headerName: "Buyer Commission %",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 160,
        minWidth: 160,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          formatDecimalAmount(row.client_buyer_rate?.percentage ?? 0, locale),
        valueSetter: (value: unknown, row: FeeRow) => ({
          ...row,
          client_buyer_rate: {
            ...(row.client_buyer_rate ?? {}),
            percentage: parseLocaleNumber(value),
          } as Rate,
        }),
      },
      {
        field: "client_seller_flat",
        headerName: "Seller Commission Flat",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 160,
        minWidth: 160,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(
            row.client_seller_rate?.flat ?? 0,
            row.currency,
            locale,
          ),
        valueSetter: (value: unknown, row: FeeRow) => {
          if (!row.currency) {
            return row;
          }
          return {
            ...row,
            client_seller_rate: {
              ...(row.client_seller_rate ?? {}),
              flat: parseMoney(String(value), row.currency, locale),
            } as Rate,
          };
        },
      },
      {
        field: "client_seller_percentage",
        headerName: "Seller Commission %",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 160,
        minWidth: 160,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          formatDecimalAmount(row.client_seller_rate?.percentage ?? 0, locale),
        valueSetter: (value: unknown, row: FeeRow) => ({
          ...row,
          client_seller_rate: {
            ...(row.client_seller_rate ?? {}),
            percentage: parseLocaleNumber(value),
          } as Rate,
        }),
      },
    ],
    [canEditFees, currencyFlagViewCell, paymentMethodChipViewCell, locale],
  );

  const displayFees = useMemo(() => {
    const originalFees = feesData?.fees || [];
    const newFees = editedFees.filter(
      (fee) => !originalFees.some((original) => original.id === fee.id),
    );
    return [...originalFees, ...newFees];
  }, [feesData?.fees, editedFees]);

  const handleRowsChange = useCallback(
    (fees: FeeRow[]) => {
      if (!canEditFees) {
        return;
      }

      const originalFeeIds = new Set(
        feesData?.fees.map((f) => f.id as string | number) || [],
      );
      const newEditingFees = new Set<string | number>();

      fees.forEach((fee) => {
        if (!originalFeeIds.has(fee.id)) {
          newEditingFees.add(fee.id);
        }
      });

      setEditingFees(newEditingFees);

      const filteredFees = fees.filter((fee) => {
        const isOriginal = originalFeeIds.has(fee.id);
        const isBeingEdited = newEditingFees.has(fee.id);
        return !isOriginal || isBeingEdited;
      });

      setEditedFees(filteredFees);
    },
    [canEditFees, feesData?.fees],
  );

  const createEmptyRow = (): FeeRow => ({
    id: crypto.randomUUID(),
    client_id: clientId,
    charge_config: 1,
    payment_method: "",
    currency: "",
    above: 0,
    flat: 0,
    percentage: 0,
  });

  const handleFeeSave = useCallback(
    (fee: FeeRow) => {
      if (!canEditFees) {
        return;
      }

      const original = feesData?.fees.find((f) => f.id === fee.id);

      if (original && canUpdateFee) {
        const hasChanges =
          (fee.client_buyer_rate?.flat ?? null) !==
            (original.client_buyer_rate?.flat ?? null) ||
          (fee.client_buyer_rate?.percentage ?? null) !==
            (original.client_buyer_rate?.percentage ?? null) ||
          (fee.client_seller_rate?.flat ?? null) !==
            (original.client_seller_rate?.flat ?? null) ||
          (fee.client_seller_rate?.percentage ?? null) !==
            (original.client_seller_rate?.percentage ?? null);

        if (hasChanges) {
          const body: IUpdateClientFeeBody = {
            client_buyer_rate: Rate.fromJS({
              flat: fee.client_buyer_rate?.flat ?? 0,
              percentage: fee.client_buyer_rate?.percentage ?? 0,
            }),
            client_seller_rate: Rate.fromJS({
              flat: fee.client_seller_rate?.flat ?? 0,
              percentage: fee.client_seller_rate?.percentage ?? 0,
            }),
          };
          updateFee({
            client_id: clientId,
            fee_id: fee.id as number,
            updateClientFeeBody: UpdateClientFeeBody.fromJS(body),
          });
        }
      }

      setEditingFees((prev) => {
        const next = new Set(prev);
        next.delete(fee.id as string | number);
        return next;
      });
    },
    [canEditFees, canUpdateFee, feesData?.fees, updateFee, clientId],
  );

  return (
    <>
      <Stack spacing={{ xs: 2, md: 3 }}>
        <EditableDataGrid<FeeRow>
          dataGridTestId="client-fees-grid"
          columns={columns}
          rows={displayFees}
          onRowsChange={handleRowsChange}
          createEmptyRow={createEmptyRow}
          loading={isFeesFetching}
          headingIconName="domain_verification"
          heading="Client Fees"
          addButtonLabel="Add a Fee"
          canEdit={canUpdateFee}
          initialState={initialState}
          zodSchema={clientFeeSchema}
          onSave={handleFeeSave}
          noRowsLabel="No Client Fees"
          deleteDialogHeading="Delete Fee"
          getDeleteDialogMessage={() => "Delete is disabled."}
          deleteDialogConfirmText="OK"
          deleteDialogCancelText="Cancel"
          canAdd={false}
          canDelete={false}
        />
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <MuiLink
            component={Link}
            to={`/clients/${clientId}`}
            data-testid="back-to-client-link"
          >
            Back to Client
          </MuiLink>
        </Stack>
      </Stack>
    </>
  );
}
